/**
 * widgetPreviewModel — builds the synthetic `WidgetConfig` that a palette card
 * (and now the dnd-kit DragOverlay) renders to preview "what will drop."
 *
 * Extracted so BOTH the palette card's inline preview AND the drag overlay use
 * ONE construction (Story 37 canvas dnd-kit migration). The overlay rendering the
 * SAME WidgetRenderer as the canvas is the WYSIWYG fix (palette drag ghost ≡
 * canvas widget). Mirrors the auto-bind heuristic a real drop applies (Story 37.7)
 * so a platform widget previews populated, exactly as it lands.
 *
 * The id is DETERMINISTIC (`preview-<type>`) — stable across renders and keeps the
 * `widgetData`-by-`widget.id` lookup in WidgetPreview correct.
 */

import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS } from "./canvasWidgetDefaults";
import { resolveDefaultMetric, applyDefaultMetricToConfig } from "../../lib/metricCompatibility";
import { getRegistryForPlatform } from "@provenleap/integrations";

/** Minimal shape the model needs from a palette item (avoids importing the full
 *  PaletteItem type from the contended WidgetPalette module). */
export interface PreviewWidgetInput {
  type: string;
  label: string;
  defaultColSpan: number;
  defaultRowSpan: number;
}

/**
 * Build the preview widget for a (possibly platform-bound) palette item.
 * When `platform` is provided, seed the auto-bound default metric so the preview
 * matches the populated widget a connected drop produces; otherwise the type's
 * blank default config is used (honest for an unconfigured drop).
 */
export function buildPreviewWidget(item: PreviewWidgetInput, platform?: string): WidgetConfig {
  const type = item.type;
  let config: Record<string, unknown> = {
    ...DEFAULT_WIDGET_CONFIGS[type as keyof typeof DEFAULT_WIDGET_CONFIGS],
    ...(platform ? { integrationId: platform } : {}),
  };
  if (platform) {
    const registry = getRegistryForPlatform(platform as Parameters<typeof getRegistryForPlatform>[0]);
    const defaultMetric = resolveDefaultMetric(type, registry?.metrics ?? []);
    config = applyDefaultMetricToConfig(
      config,
      type,
      defaultMetric
        ? { key: defaultMetric.key, label: defaultMetric.label, integrationId: platform }
        : undefined,
    );
  }
  return {
    id: `preview-${type}`,
    type,
    title: item.label,
    position: { row: 0, col: 0, colSpan: item.defaultColSpan, rowSpan: item.defaultRowSpan },
    config,
  };
}
